import { type FunctionComponent, type ReactNode } from 'react';
import cn from 'classnames';
import { Card, HStack, Island, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { ErrorBlankSad, ErrorBlankTears } from '../../../ErrorBlank';
import { ErrorBoundaryWrapper } from '../../../ErrorBoundary';
import { StatusWrapper } from '../../../StatusWrapper';
import styles from './GridLayout.module.css';

export interface GridLayoutProps extends TestProps {
    pagination?: ReactNode;
    isPaginationVisible?: boolean;
    multiActions?: ReactNode;
    isMultiActionsVisible?: boolean;
    containerClassName?: string;
    paginationClassName?: string;
    isEmpty?: boolean;
    renderEmptyIslandContent?: () => ReactNode;
    isError?: boolean;
    renderErrorIslandContent?: () => ReactNode;
    children: (gridProps: { className: string }) => ReactNode;
}

export const GridLayout: FunctionComponent<GridLayoutProps> = ({
    'data-testid': dataTestId = 'GridLayout',
    pagination,
    multiActions,
    isMultiActionsVisible,
    isPaginationVisible,
    containerClassName,
    paginationClassName,
    children,
    isEmpty,
    isError,
    renderEmptyIslandContent,
    renderErrorIslandContent,
}) => {
    const renderEmpty = () => (
        <Island
            data-testid={`${dataTestId}.Empty`}
            className={styles.island}
            alignItems="center"
            justifyContent="center"
        >
            {renderEmptyIslandContent ? renderEmptyIslandContent() : <ErrorBlankSad title="Данных нет" />}
        </Island>
    );

    const renderError = () => (
        <Island
            data-testid={`${dataTestId}.Error`}
            className={styles.island}
            alignItems="center"
            justifyContent="center"
        >
            {renderErrorIslandContent ? renderErrorIslandContent() : <ErrorBlankTears title="Данных нет" />}
        </Island>
    );

    const hasPagination = pagination && isPaginationVisible;

    const hasMultiActions = multiActions && isMultiActionsVisible;
    const debouncedHasMultiActions = useDebouncedValue(hasMultiActions, 250);
    const isMultiActionsMount = hasMultiActions || debouncedHasMultiActions;

    return (
        <StatusWrapper isEmpty={isEmpty} renderEmpty={renderEmpty} renderError={renderError} isError={isError}>
            <ErrorBoundaryWrapper errorFallback={renderError}>
                <div data-testid={dataTestId} className={cn(styles.container, containerClassName)}>
                    {children({
                        className: cn(styles.grid, { [styles.withPagination]: hasPagination }),
                    })}
                    {hasPagination && (
                        <Card color="white" padding="8" className={cn(styles.pagination, paginationClassName)}>
                            <VStack alignItems="flex-start" justifyContent="center">
                                {pagination}
                            </VStack>
                        </Card>
                    )}
                    {isMultiActionsMount && (
                        <HStack
                            className={cn(styles.multiActions, { [styles.multiActionsVisible]: hasMultiActions })}
                            justifyContent="center"
                        >
                            {multiActions}
                        </HStack>
                    )}
                </div>
            </ErrorBoundaryWrapper>
        </StatusWrapper>
    );
};
